/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any */
// @ts-nocheck
import * as fc from 'd3fc';
import * as d3 from 'd3';
import { BandsResult, MACDResult } from 'trading-signals';

import { ColorMode } from 'theme';

import { SeriesSvgLines } from 'components/sg_d3fc/lines';
import { SymbolLineParams } from 'types';
import { CandleTOHLCVSDG } from 'HiroDatafeed';
import { IndicatorMeta } from './controls/AvailableIndicators';

export class ChartSeries {
  name: string;

  // get the d3fc series to be instantiated in the d3fc seriesSvgMutlti
  abstract d3fcSeries(
    xDomain,
    priceScaleRef,
    chartXScaleRef,
    chartYScaleRef,
    priceInDeltaScale,
    candleWidth,
    colorMode,
    hiroLineType,
  ): any[];

  // get the d3 data to be joined to the d3fc series
  abstract d3Data(): any;

  yExtentAccessors?(): [
    (series: any) => number | null,
    (series: any) => number | null,
  ];

  id(suffix): string {
    return `${this.name.replace(/[\W]/g, '_')}_${suffix}`;
  }

  numD3fcSeries(): number {
    return 1;
  }
}

export class DeltaChartSeries extends ChartSeries {
  symbolLineParams: SymbolLineParams;
  candles: CandleTOHLCVSDG[];
  colors: string[];

  constructor(
    name: string,
    symbolLineParams: SymbolLineParams,
    candles: CandleTOHLCVSDG[],
    colors: string[],
  ) {
    super();
    this.name = name;
    this.symbolLineParams = symbolLineParams;
    this.candles = candles;
    this.colors = colors;
  }

  d3fcSeries(
    xDomain,
    priceScaleRef,
    chartXScaleRef,
    chartYScaleRef,
    priceInDeltaScale,
    candleWidth,
    _colorMode,
    hiroLineType,
  ) {
    let fcSeries;
    switch (hiroLineType) {
      case 'Line':
        fcSeries = fc
          .seriesSvgLine()
          .crossValue((d) => d.date)
          .mainValue((d) => d.close);
        break;
      case 'OHLC':
        fcSeries = fc.seriesSvgOhlc().bandwidth(() => candleWidth);
        break;
      case 'Candle':
      default:
        fcSeries = fc.seriesSvgCandlestick().bandwidth(() => candleWidth);
        break;
    }
    return [
      fcSeries.decorate((selection, _datum, _index) => {
        const parentNode = selection.node()?.parentNode;
        if (parentNode != null) {
          d3.select(parentNode)
            .attr('id', this.id(hiroLineType))
            .attr('class', 'chart-series-group');
        }

        selection
          .enter()
          .attr('stroke', this.colors[0])
          .attr('fill', this.colors[0])
          .select('path')
          .attr('stroke', this.colors[0])
          .attr('fill', this.colors[0]);
      }),
    ];
  }

  d3Data() {
    return this.candles;
  }
}

export class PriceChartSeries extends ChartSeries {
  latestValueLabelColor: string;
  priceLineType: string;
  constructor(
    name: string,
    symbolLineParams: SymbolLineParams,
    candles: CandleTOHLCVSDG[],
    colors: string[],
    latestValueLabelColor: string,
    priceLineType: string = 'Line',
  ) {
    super();
    this.name = name;
    this.symbolLineParams = symbolLineParams;
    this.candles = candles;
    this.colors = colors;
    this.latestValueLabelColor = latestValueLabelColor;
    this.priceLineType = priceLineType;
  }

  d3fcSeries(
    xDomain,
    priceScaleRef,
    chartXScaleRef,
    chartYScaleRef,
    priceInDeltaScale,
    candleWidth,
    _colorMode,
    _hiroLineType,
  ) {
    const lineType = this.priceLineType;
    let fcSeries;
    switch (lineType) {
      case 'OHLC':
        fcSeries = fc
          .seriesSvgOhlc()
          .bandwidth(() => candleWidth)
          .openValue((d) => priceInDeltaScale(d.open))
          .closeValue((d) => priceInDeltaScale(d.close))
          .highValue((d) => priceInDeltaScale(d.high))
          .lowValue((d) => priceInDeltaScale(d.low));
        break;
      case 'Candle':
        fcSeries = fc
          .seriesSvgCandlestick()
          .bandwidth(() => candleWidth)
          .openValue((d) => priceInDeltaScale(d.open))
          .closeValue((d) => priceInDeltaScale(d.close))
          .highValue((d) => priceInDeltaScale(d.high))
          .lowValue((d) => priceInDeltaScale(d.low));
        break;
      case 'Line':
      default:
        fcSeries = fc
          .seriesSvgLine()
          .mainValue((d) => priceInDeltaScale(d.close));
        break;
    }
    return [
      fcSeries
        .crossValue((d) => d.date)
        .xScale(xDomain)
        .yScale(priceScaleRef.current)
        .decorate((selection) => {
          const parentNode = selection.node()?.parentNode;
          if (parentNode != null) {
            d3.select(parentNode)
              .attr('id', this.id(this.priceLineType))
              .attr('class', 'chart-series-group');
          }
          selection
            .enter()
            .attr('stroke', 'currentColor')
            .attr('fill', 'currentColor')
            .select('path')
            .attr('stroke', 'currentColor')
            .attr('fill', 'currentColor');
        }),
    ];
  }

  d3Data() {
    return this.candles;
  }

  yExtent(): [number, number] {
    return candlesYExtent(this.candles);
  }
}

export class KeyLevelChartSeries extends ChartSeries {
  keyLevels: ChartKeyLevel[];

  constructor(name: string, keyLevels: ChartKeyLevel[]) {
    super();
    this.name = 'hiroKeyLevels';
    this.keyLevels = keyLevels;
  }

  d3fcSeries(
    xDomain,
    priceScaleRef,
    chartXScaleRef,
    chartYScaleRef,
    priceInDeltaScale,
    _candleWidth,
    _colorMode,
    hiroLineType,
  ) {
    return [
      SeriesSvgLines(chartXScaleRef)
        .x0Value((d) => d.begDate)
        .y0Value((d) => priceInDeltaScale(d.price))
        .x1Value((d) => d.endDate)
        .y1Value((d) => priceInDeltaScale(d.price))
        .decorate((selection, _datum, _index) => {
          const parentNode = selection.node()?.parentNode;
          if (parentNode != null) {
            d3.select(parentNode)
              .attr('id', this.id(hiroLineType))
              .attr('class', 'chart-series-group');
          }
        })
        .decorateEach((groupSel, d) => {
          const begX = chartXScaleRef.current(d.begDate);
          const endX = chartXScaleRef.current(d.endDate);
          groupSel.attr('class', 'lines label-hover-detector');
          let text = groupSel.select('text');
          if (text.size() == 0) {
            text = groupSel
              .append('text')
              .attr('font-size', '11')
              .attr('fill', d.color)
              .attr('class', 'label hovered-label-to-display')
              .attr('opacity', '0.8');
          }
          if (endX > 20) {
            const labelX = Math.max(begX, 9);
            const labelY =
              chartYScaleRef.current(priceInDeltaScale(d.price)) - 2;
            text.text(genKeyLevelLabel(d.keyLevel, d.price));
            text.attr('transform', `translate(${labelX}, ${labelY})`);
          } else {
            text.text('');
          }
          //console.log(v);
          groupSel.select('path').attr('stroke', d.color);
        }),
    ];
  }

  d3Data() {
    return this.keyLevels;
  }
}

interface AlertSeriesData {
  alert_time: Date;
  category: HiroAlertCategory;
}

export class AlertsChartSeries extends ChartSeries {
  alerts: AlertSeriesData[];

  constructor(alerts: AlertSeriesData[]) {
    super();
    this.name = 'hiroAlerts';
    this.alerts = alerts;
  }

  d3fcSeries(
    xDomain,
    priceScaleRef,
    chartXScaleRef,
    chartYScaleRef,
    _priceInDeltaScale,
    _candleWidth,
    colorMode,
    hiroLineType,
  ) {
    return [
      SeriesSvgLines()
        .x0Value((d) => d.alert_time)
        .y0Value((_d) => 1e12)
        .x1Value((d) => d.alert_time)
        .y1Value((_d) => -1e12)
        .decorate((selection, _datum, _index) => {
          selection.enter().attr('class', 'lines label-hover-detector');

          const parentNode = selection.node()?.parentNode;
          if (parentNode != null) {
            d3.select(parentNode)
              .attr('id', this.id(hiroLineType))
              .attr('class', 'chart-series-group');
          }
        })
        .decorateEach((groupSel, d) => {
          const lineX = chartXScaleRef.current(d.alert_time);
          const categoryTokens = d.category
            .replace(/ *(Negative|Positive) */g, '')
            .split(' ');

          const textLines = groupSel.selectAll('text').data(categoryTokens);
          groupSel.selectAll('path').attr('stroke-width', '2');

          textLines
            .enter()
            .append('text')
            .attr('fill', colorMode == ColorMode.LIGHT ? '#0a8f58' : '#6cf5bb')
            .attr('class', 'label hovered-label-to-display')
            .attr('opacity', '0.8')
            .attr('text-anchor', 'middle')
            .attr('font-size', '11');

          textLines.exit().remove();

          textLines
            .merge(textLines)
            .text((d) => d)
            .attr('transform', (d, i) => `translate(${lineX}, ${28 + 12 * i})`);

          groupSel
            .select('path')
            .attr(
              'stroke',
              colorMode == ColorMode.LIGHT ? '#0a8f58aa' : '#6cf5bb40',
            );
        }),
    ];
  }

  d3Data() {
    return this.alerts;
  }
}

interface IndicatorSingleLineChartSeriesData {
  date: Date;
  value: number;
}
export class IndicatorSingleLineChartSeries extends ChartSeries {
  name: string;
  yScale: 'price' | 'delta' | 'own';
  indicatorMeta: IndicatorMeta;
  data: IndicatorSingleLineChartSeriesData[];
  colors: string[];

  constructor(
    name: string,
    yScale: 'price' | 'delta',
    indicatorMeta: any,
    data: IndicatorSingleLineChartSeriesData[],
    colors: string[],
  ) {
    super();
    this.name = name;
    this.yScale = yScale;
    this.indicatorMeta = indicatorMeta;
    this.data = data;
    this.colors = colors;
  }

  d3fcSeries(
    xDomain,
    priceScaleRef,
    chartXScaleRef,
    chartYScaleRef,
    priceInDeltaScale,
    _candleWidth,
    _colorMode,
    hiroLineType,
  ) {
    return [
      fc
        .seriesSvgLine()
        .crossValue((d) => d.date)
        .mainValue((d) =>
          this.yScale == 'price' ? priceInDeltaScale(d.value) : d.value,
        )
        .xScale(xDomain)
        .yScale(this.yScale == 'price' ? priceScaleRef.current : chartYScaleRef)
        .decorate((selection, _datum, _index) => {
          const parentNode = selection.node()?.parentNode;
          if (parentNode != null) {
            d3.select(parentNode)
              .attr('id', this.id(hiroLineType))
              .attr('class', 'chart-series-group');
          }
          selection.attr('stroke', this.colors[0]);
        }),
    ];
  }

  d3Data() {
    return this.data;
  }
}

type BollingerBandsDatum = BandsResult & { date: Date };

export class BollingerBandsChartSeries extends ChartSeries {
  name: string;
  yScale: 'price' | 'delta' | 'own';
  indicatorMeta: IndicatorMeta;
  data: BollingerBandsDatum[];
  colors: string[];

  constructor(
    name: string,
    yScale: 'price' | 'delta',
    indicatorMeta: any,
    data: BollingerBandsDatum[],
    colors: string[],
  ) {
    super();
    this.name = name;
    this.yScale = yScale;
    this.indicatorMeta = indicatorMeta;
    this.data = data;
    this.colors = colors;
  }

  numD3fcSeries(): number {
    return 4;
  }

  d3fcSeries(
    xDomain,
    priceScaleRef,
    chartXScaleRef,
    chartYScaleRef,
    priceInDeltaScale,
    _candleWidth,
    _colorMode,
    hiroLineType,
  ) {
    const yConversion = (v) =>
      this.yScale == 'price' ? priceInDeltaScale(v) : v;
    return [
      fc
        .seriesSvgArea()
        .mainValue((d) => yConversion(d.upper))
        .baseValue((d) => yConversion(d.lower)),

      fc.seriesSvgLine().mainValue((d) => yConversion(d.upper)),

      fc.seriesSvgLine().mainValue((d) => yConversion(d.middle)),

      fc.seriesSvgLine().mainValue((d) => yConversion(d.lower)),
    ].map((fcSeries, fcSeriesIdx) =>
      fcSeries
        .crossValue((d) => d.date)
        .xScale(xDomain)
        .yScale(this.yScale == 'price' ? priceScaleRef.current : chartYScaleRef)
        .decorate((selection, _datum, _index) => {
          const parentNode = selection.node()?.parentNode;
          if (parentNode != null) {
            d3.select(parentNode)
              .attr('id', this.id(hiroLineType))
              .attr('class', 'chart-series-group');
          }
          selection
            .attr('stroke', this.colors[0])
            .attr('fill', this.colors[0])
            .attr('opacity', fcSeriesIdx == 0 ? '0.2' : '1');
        }),
    );
  }

  d3Data() {
    return this.data;
  }
}

type MACDDatum = MACDResult & { date: Date };

export class MACDChartSeries extends ChartSeries {
  name: string;
  yScale: 'price' | 'delta' | 'own';
  indicatorMeta: IndicatorMeta;
  data: MACDDatum[];
  colors: string[];
  colorFromName: Record<string, string>;

  constructor(
    name: string,
    yScale: 'own',
    indicatorMeta: any,
    data: MACDDatum[],
    colors: string[],
  ) {
    super();
    this.name = name;
    this.yScale = yScale;
    this.indicatorMeta = indicatorMeta;
    this.data = data;
    this.colors = colors;
    this.colorFromName = Object.fromEntries(
      indicatorMeta.indicatorOutputs!.map((indicatorOutput, i) => [
        indicatorOutput.name,
        colors[i],
      ]),
    );
  }

  numD3fcSeries(): number {
    return 3;
  }

  d3fcSeries(
    xDomain,
    priceScaleRef,
    chartXScaleRef,
    chartYScaleRef,
    priceInDeltaScale,
    candleWidth,
    _colorMode,
    hiroLineType,
  ) {
    return [
      ['Histogram', fc.seriesSvgBar().mainValue((d) => d.histogram)],

      ['Signal', fc.seriesSvgLine().mainValue((d) => d.signal)],

      ['MACD', fc.seriesSvgLine().mainValue((d) => d.macd)],
    ].map(([name, fcSeries]) =>
      fcSeries
        .crossValue((d) => d.date)
        .xScale(xDomain)
        .yScale(chartYScaleRef)
        .decorate((selection, _datum, _index) => {
          const parentNode = selection.node()?.parentNode;
          if (parentNode != null) {
            d3.select(parentNode)
              .attr('id', this.id(hiroLineType))
              .attr('class', 'chart-series-group');
          }
          selection
            .attr('stroke', this.colorFromName[name])
            .attr('fill', this.colorFromName[name]);
        }),
    );
  }

  d3Data() {
    return this.data;
  }
}

const genKeyLevelLabel = (k: PrikeLineKey, price: number): string => {
  const formattedPrice = price.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  return `${k} $${formattedPrice}`;
};

export const candlesYExtentAccessors = [
  (series: DeltaChartSeries | PriceChartSeries) =>
    series.candles.reduce(
      (accum, c) => Math.max(accum ?? c.high, c.high),
      null,
    ),
  (series: DeltaChartSeries | PriceChartSeries) =>
    series.candles.reduce((accum, c) => Math.min(accum ?? c.low, c.low), null),
];

type IndicatorTypes =
  | IndicatorSingleLineChartSeries
  | BollingerBandsChartSeries
  | MACDChartSeries;

export const indicatorYExtentAccessors = [
  (series: IndicatorTypes) => {
    if (series instanceof IndicatorSingleLineChartSeries) {
      return series.data.reduce(
        (accum, c) => Math.max(accum ?? c.value, c.value),
        null,
      );
    } else if (series instanceof BollingerBandsChartSeries) {
      return series.data.reduce(
        (accum, c) => Math.max(accum ?? c.upper, c.upper),
        null,
      );
    } else if (series instanceof MACDChartSeries) {
      return series.data.reduce(
        (accum, c) =>
          Math.max(
            accum ?? c.histogram ?? c.macd ?? c.signal,
            c.histogram,
            c.macd,
            c.signal,
          ),
        null,
      );
    }
  },
  (series: IndicatorTypes) => {
    if (series instanceof IndicatorSingleLineChartSeries) {
      return series.data.reduce(
        (accum, c) => Math.min(accum ?? c.value, c.value),
        null,
      );
    } else if (series instanceof BollingerBandsChartSeries) {
      return series.data.reduce(
        (accum, c) => Math.min(accum ?? c.lower, c.lower),
        null,
      );
    } else if (series instanceof MACDChartSeries) {
      return series.data.reduce(
        (accum, c) =>
          Math.min(
            accum ?? c.histogram ?? c.macd ?? c.signal,
            c.histogram,
            c.macd,
            c.signal,
          ),
        null,
      );
    }
  },
];
